<script>
  import { format } from 'svelte-i18n';
  import { Alert, Input, Tooltip } from '@sveltestrap/sveltestrap';

  import { CsvPreview } from '@client/components/csv-preview';
  import { FileInput } from '@client/components/file-input';
  import { Icon } from '@client/components/icon';
  import { Section, SectionItem } from '@client/components/section';
  import { Select } from '@client/components/select';
  import { ShowMore } from '@client/components/show-more';
  import { Spinner } from '@client/components/spinner';
  import { UserSelect } from '@client/components/user-select';

  import { csvStore } from '@client/stores/csv';
  import { syncStore } from '@client/stores/sync';

  import CsvStatus from '../csv-status.svelte';
  import Status from '../status.svelte';

  export let loading = false;
  export let sources = [];
  export let types = [];
  export let config = {};
  export let syncing = false;
  export let isCSV = false;
  export let selectedUser = null;
  export let fieldOptions = [];
  export let mergeOptions = [];
  export let nosyncOptions = [];

  export let onSourceChange = () => {};
  export let onTypeChange = () => {};
  export let onMergeChange = () => {};
  export let onNoSyncChange = () => {};
  export let onDefaultLockedChange = () => {};
  export let onSelectUsers = () => {};
  export let resetUserSelect = () => {};
  export let handleFileInput = () => {};
  export let reset = () => {};
</script>

{#if loading}
  <div class="loading">
    <div>
      <Spinner show inline />
      {$format('label.CHECKING_CONNECTIONS')}
    </div>
  </div>
{:else if !sources?.length}
  <Alert color="danger">{$format('label.NO_SYNC_SOURCES')}</Alert>
{:else if syncing}
  {#if $syncStore.error}
    <Alert color="danger">{$syncStore.error}</Alert>
  {:else}
    <Status
      failures={$syncStore.failures}
      syncing={$syncStore.syncing}
      results={$syncStore.syncResults}
      successes={$syncStore.successes}
      total={isCSV && $csvStore.parsed?.data.length}
      type={config.type?.value.toUpperCase()}
    />
  {/if}
{:else}
  <p class="mb-3">{$format('label.DESCRIPTION.SYNC')}</p>

  <div class="section">
    <SectionItem
      label={$format('label.SOURCE')}
      tooltip={$format('label.TOOLTIP.SOURCE_SYNC')}
      borderless
      fill
      htmlFor="sync-source"
    >
      <Select
        id="sync-source"
        items={sources}
        bind:value={config.source}
        on:change={onSourceChange}
        placeholder={$format('label.PLACEHOLDER.SELECT_SYNC_SOURCE')}
        clearable={false}
        showChevron
        searchable
      />
    </SectionItem>
  </div>

  {#if !isCSV}
    <div class="section">
      <SectionItem
        label={$format('label.TYPE')}
        tooltip={$format('label.TOOLTIP.TYPE_SYNC')}
        borderless
        fill
        htmlFor="sync-type"
      >
        <Select
          id="sync-type"
          items={types}
          bind:value={config.type}
          on:change={onTypeChange}
          placeholder={$format('label.PLACEHOLDER.SELECT_SYNC_TYPE')}
          clearable={false}
          showChevron
          searchable
        />
      </SectionItem>
    </div>

    {#if config.type?.value === 'individual'}
      <div class="section">
        <SectionItem
          label={$format('label.USER')}
          tooltip={$format('label.PLACEHOLDER.SYNC_INDIVIDUAL_USER')}
          borderless
          fill
          htmlFor="sync-users"
        >
          <UserSelect
            id="sync-users"
            placeholder={$format('label.PLACEHOLDER.SYNC_INDIVIDUAL_USER')}
            bind:value={selectedUser}
            on:change={onSelectUsers}
            on:clear={resetUserSelect}
          />
        </SectionItem>
      </div>
    {/if}
  {:else}
    {#if config.source?.value === 'manual'}
      <Input
        type="textarea"
        class="font-monospace"
        invalid={$csvStore.raw && $csvStore.parsed?.errors?.length}
        placeholder="username,name,title"
        rows={4}
        on:input={(e) => {
          csvStore.setCsv(e.target.value);
        }}
      />
    {:else}
      <div class="section">
        <SectionItem
          label={$format('label.SPREADSHEET_FILE')}
          tooltip={$format('label.TOOLTIP.CSV_FILE')}
          borderless
          class="pb-0"
          fill
          htmlFor="csv"
        >
          <FileInput
            accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            id="csv"
            onLoad={handleFileInput}
            onRemove={() => reset()}
          />
        </SectionItem>
      </div>
    {/if}

    {#if $csvStore.raw && $csvStore.parsed}
      <div class="section">
        <CsvStatus
          errors={$csvStore.parsed.errors}
          headers={$csvStore.parsed.meta?.fields}
          rows={$csvStore.parsed.data}
          class="pt-2"
        />
        <CsvPreview
          errors={$csvStore.parsed.errors}
          headers={$csvStore.parsed.meta?.fields}
          rows={$csvStore.parsed.data}
        />
      </div>
    {/if}
  {/if}

  {#if config.type?.value === 'users' || isCSV}
    <ShowMore collapsedText="Show Options" expandedText="Hide Options">
      <h5 class="mt-3 mb-2">
        {$format('label.OPTIONS')}
        <Icon id="options-tooltip" name="help-circle" />
        <Tooltip target="options-tooltip" placement="top">
          {$format('label.TOOLTIP.OPTIONS_SYNC')}
        </Tooltip>
      </h5>

      <Section class="p-3">
        <SectionItem
          label={$format('label.FIELD_MERGING')}
          tooltip={$format('label.TOOLTIP.FIELD_MERGING')}
          borderless
          class="pb-0"
          fill
          htmlFor="field-merging"
        >
          <Select
            id="field-merging"
            items={mergeOptions}
            bind:value={config.replace}
            on:change={onMergeChange}
            placeholder={$format('label.PLACEHOLDER.SELECT_MERGE_ACTION')}
            clearable={false}
            showChevron
            searchable
          />
          {#if config.replace?.value}
            <br />
            <Alert color="danger">{$format('label.SELECT_MERGE_ACTION_WARNING')}</Alert>
          {/if}
        </SectionItem>
        <SectionItem
          label={$format('label.NO_SYNC_FIELDS')}
          tooltip={$format('label.TOOLTIP.NO_SYNC_FIELDS')}
          borderless
          class="pt-3 pb-0"
          fill
          htmlFor="no-sync-fields"
        >
          <Select
            id="no-sync-fields"
            items={nosyncOptions}
            bind:value={config.ignorenosync}
            on:change={onNoSyncChange}
            placeholder={$format('label.PLACEHOLDER.SELECT_NO_SYNC_ACTION')}
            clearable={false}
            showChevron
            searchable
          />
        </SectionItem>
        <SectionItem
          label={$format('label.DEFAULT_LOCKED')}
          tooltip={$format('label.TOOLTIP.DEFAULT_LOCKED')}
          borderless
          class="pt-3"
          fill
          htmlFor="default-locked"
        >
          <Select
            clearable={false}
            id="default-locked"
            items={fieldOptions}
            placeholder={$format('label.PLACEHOLDER.SELECT_NO_SYNC_ACTION')}
            searchable
            showChevron
            bind:value={config.defaultlocked}
            on:change={onDefaultLockedChange}
          />
        </SectionItem>
      </Section>
    </ShowMore>
  {/if}
{/if}

<style lang="scss">
  .loading {
    align-items: center;
    color: var(--color-gray-800);
    display: flex;
    flex-direction: column;

    > div {
      align-items: center;
      display: flex;
      gap: 8px;
      min-height: 300px;
      justify-content: center;
    }
  }
</style>
